import React, { useEffect, useState } from 'react';
import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Modal from '../common/Modal';
import CustomUploadField from '../shared/CustomUploadField';
import { DIMENSIONS } from '../../constants/aspectRatio';
import { useExerciseCategory } from './hooks/useExerciseCategory';
import CustomInfoButton from '../shared/CustomInfoButton';
import { CONTENT_TYPE, CONTENT_TYPE_ID, TOOLTIP_MSG_ADVANCED, TOOLTIP_MSG_INTERMEDIATE } from './contants';
import CustomMultiSelect from '../shared/CustomMultiSelect';
import CustomRadioGroup from '../shared/CustomRadioGroup';
import CustomPopupText from '../shared/CustomPopupText';
import CustomDropDown from '../shared/CustomDropDown';

const useStyle = makeStyles({
  popupContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  header: {
    fontWeight: '600',
    fontSize: '18px',
    padding: '0px 10px',
  },
  content: {
    height: '60vh',
    overflowY: 'auto',
    background: '#f3f4f7',
    padding: '20px 10px',
    gap: '24px',
  },
  required_field: {
    color: '#667085',
    '&:after': {
      content: "' *'",
      color: '#F50000',
    },
  },
  option_field: {
    color: '#667085',
  },
  inputField: {
    width: '97%',
    padding: '15px 10px',
    borderRadius: '5px',
    border: '1px solid #E4E7EC',
    outline: 'none',
    marginTop: '5px',
    background: 'white',
    fontSize: '14px',
  },
  upload: {
    border: '1px solid #E4E7EC',
    padding: '8px 10px',
    marginTop: '5px',
    width: '101%',
    borderRadius: '5px',
    background: 'white',
  },
  textArea: {
    width: '96%',
    height: '70px',
    padding: '15px 10px',
    borderRadius: '5px',
    border: '1px solid #E4E7EC',
    outline: 'none',
    marginTop: '5px',
    background: 'white',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'sans-serif',
  },
  smalltext: {
    font: '14px',
    color: '#667085',
    marginRight: '5px',
  },
});

export const ExerciseCatPopup = ({ isOpen, setIsOpen, isEdit }) => {
  const classes = useStyle();

  const {
    form,
    handleForm,
    handleSave,
    imageData,
    thumbnail,
    thumbnailImg,
    rnpList,
    selectedRnp,
    contentType,
    handleImageChange,
    handleDeleteThumbnail,
    fetchCategoryInfo,
    resetAll,
    departmentsList,
    fetchAllAiRolePlayDepartments,
    handleDepartmentChange,
    fetchRockedNetworkPartners,
    setSelectedRnp,
    setContentType,
  } = useExerciseCategory();

  const [rnpOptions, setRnpOptions] = useState([]);

  useEffect(() => {
    if (parseInt(contentType, 10) === CONTENT_TYPE_ID.OEM) {
      setRnpOptions(rnpList?.OEM?.partners);
      return;
    }

    if (parseInt(contentType, 10) === CONTENT_TYPE_ID.PARTNER) {
      setRnpOptions(rnpList?.PARTNER?.partners);
      return;
    }

    setRnpOptions([]);
  }, [contentType, rnpList]);

  const handleClose = () => {
    setIsOpen(false);
    resetAll();
  };

  useEffect(() => {
    fetchRockedNetworkPartners();
    if (isEdit) {
      fetchCategoryInfo();
    }
  }, []);

  useEffect(() => {
    fetchAllAiRolePlayDepartments();
  }, []);

  return (
    <Modal isOpen={isOpen} height="72vh" width="550px">
      <Grid className={classes.popupContainer}>
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
          <Grid item>
            <Grid item className={classes.header}>
              {isEdit ? 'Edit Exercise Category' : 'Add Exercise Category'}
            </Grid>
          </Grid>
          <Grid item>
            <CloseIcon onClick={handleClose} style={{ cursor: 'pointer' }} />
          </Grid>
        </Grid>

        <Grid container className={classes.content}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.required_field}>Exercise Category Name </Typography>
              </Grid>
              <Grid item xs={12}>
                <input
                  placeholder="Enter Exercise Category name"
                  className={classes.inputField}
                  value={form.name}
                  onChange={(e) => handleForm(e, 'name')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.required_field}>Thumbnail (max size - 100kb) </Typography>
              </Grid>
              <Grid item xs={12}>
                <CustomUploadField
                  type="image"
                  name="thumbnail"
                  value={thumbnailImg}
                  imgSrc={!imageData ? thumbnail : null}
                  handleChange={handleImageChange}
                  customStyles={{ width: '100%' }}
                  fileName={imageData?.name}
                  dimensions={DIMENSIONS.aiExerciseCategory}
                  handleDelete={handleDeleteThumbnail}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.required_field}>Departments</Typography>
              </Grid>
              <Grid item xs={12} style={{ marginTop: '5px' }}>
                <CustomMultiSelect
                  list={departmentsList}
                  selectedValue={form.departmentIds}
                  handleFilterChange={handleDepartmentChange}
                  name="departments"
                  width="100%"
                  height="48px"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} style={{ overflow: 'hidden' }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <CustomPopupText text="Content Type" required />
              </Grid>
              <Grid item>
                <Grid container>
                  <CustomRadioGroup
                    name="type"
                    value={contentType}
                    options={CONTENT_TYPE}
                    handleChange={(event) => {
                      setContentType(event.target.value);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} style={[2, 3].includes(parseInt(contentType, 10)) ? {} : { display: 'none' }}>
            <Grid item xs={12}>
              <CustomPopupText
                text={`Select ${CONTENT_TYPE?.find((item) => item.value === parseInt(contentType, 10))?.name}`}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomDropDown
                width="100%"
                name="contentType"
                list={rnpOptions}
                value={rnpOptions?.find((option) => option.id === parseInt(selectedRnp, 10))?.name}
                handleChange={(e) => setSelectedRnp(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Typography className={classes.required_field}>Exercise Description </Typography>
                  <Typography className={classes.smalltext}>{form.subtext?.length}/100</Typography>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <textarea
                  placeholder="Enter exercise description"
                  className={classes.textArea}
                  style={{ height: '50px' }}
                  value={form.subtext}
                  maxLength={100}
                  onChange={(e) => handleForm(e, 'subtext', 100)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Typography> Score to unlock Beginner Level (percentage)</Typography>
              </Grid>
              <Grid item xs={12}>
                <input
                  placeholder="50"
                  value={0}
                  onChange={(e) => handleForm(e, 'beginnerUnlockScore')}
                  className={classes.inputField}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                  <Typography className={classes.required_field}>
                    Score to unlock Intermediate Level (percentage)
                  </Typography>
                  <CustomInfoButton msg={TOOLTIP_MSG_INTERMEDIATE} />
                </div>
              </Grid>
              <Grid item xs={12}>
                <input
                  placeholder="100"
                  value={form.intermediateUnlockScore}
                  onChange={(e) => handleForm(e, 'intermediateUnlockScore')}
                  className={classes.inputField}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                  <Typography className={classes.required_field}>
                    Score to unlock Advanced Level (percentage)
                  </Typography>
                  <CustomInfoButton msg={TOOLTIP_MSG_ADVANCED} />
                </div>
              </Grid>
              <Grid item xs={12}>
                <input
                  placeholder="200"
                  value={form.advanceUnlockScore}
                  onChange={(e) => handleForm(e, 'advanceUnlockScore')}
                  className={classes.inputField}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid style={{ display: 'flex' }}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <>
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: '10px' }}
                onClick={() => handleSave(handleClose, isEdit)}
              >
                Save
              </Button>
            </>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};
